/**
 * Zod schema for the `authAccounts` collection (ADR-136).
 *
 * Thin auth-account pointer — one per Firebase Auth user. Exists for ONE
 * reason: cheap uid → personId resolution at the security-rules layer.
 * Rules can't query, only get(); having a uid-keyed doc with the personId
 * makes the indirection a single get() per rule evaluation.
 *
 * Document ID = Firebase Auth uid.
 *
 * Created by activateAccount inside the same transaction that sets
 * `persons.authUid`. Updated on sign-in to refresh `lastSignInAt`.
 * `profileReviewed` is the onboarding gate flag (replaces
 * `users.profileReviewed`).
 *
 * Not a profile record. Name, photo, contact info, push tokens all live
 * on `persons` / `personProfiles`. This doc holds only the personId
 * pointer + auth-account-specific state.
 */

import { z } from 'zod';
import type { TimestampLike } from '../types/common';

export const authAccountDocumentSchema = z.object({
  /** FK to `persons/{personId}`. The only load-bearing field. */
  personId: z.string(),

  /**
   * Onboarding gate flag. Mobile checks this after auth to decide
   * whether to route to ProfileReview (false) or MainTabs (true).
   * Replaces `users.profileReviewed`.
   */
  profileReviewed: z.boolean(),

  /** Most recent successful sign-in for this auth account. */
  lastSignInAt: z.custom<TimestampLike>().nullable(),
});
